import { useState } from 'react';
import PropTypes from 'prop-types';
import { postJson } from '../global/func';
import logo from '../assets/images/logo-mark.png';

// Must match PasswordReset::MIN_PASSWORD_LENGTH. Checked here so a customer is
// told before they submit, and again on the server, which is the one that counts.
const MIN_LENGTH = 10;

// Choosing the new password, having arrived from the emailed link.
//
// Failures here are specific, unlike the request step: whoever is on this screen
// already holds the token, so telling them it has expired reveals nothing they
// could not learn by trying it, and leaving them guessing is how someone ends up
// locked out believing the reset is broken.
const ResetPassword = ({ token, onDone }) => {

    const [password, setPassword] = useState('');
    const [confirm, setConfirm] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [done, setDone] = useState('');

    const submit = async (e) => {
        e.preventDefault();

        if (password.length < MIN_LENGTH) {
            setError(`Choose a password of at least ${MIN_LENGTH} characters.`);
            return;
        }
        if (password !== confirm) {
            setError('The two passwords do not match.');
            return;
        }

        setBusy(true);
        setError('');

        const url = encodeURI(import.meta.env.REACT_APP_API_SERVER_URL + '/auth/reset/');
        const result = await postJson(url, { token, password });

        setBusy(false);

        if (result.ok) {
            setDone(result.data?.message || 'Your password has been changed.');
            return;
        }

        if (result.status === 0) {
            setError('Could not reach the server. Check your connection and try again.');
            return;
        }

        setError(result.data?.error || 'That reset link could not be used. Please request another.');
    };

    return (
        <div className="column auth">
            <img className="auth__logo" src={logo} alt="Prayer Box" />
            <div>
                <h1 className="auth__title">
                    Choose a new password

                </h1>
                <div>

                    {done
                        ? <>
                            <div className="notice notice--info" role="status">{done}</div>
                            <button type="button"
                                className="btn btn--primary btn--block"
                                onClick={onDone}>
                                Sign in
                            </button>
                          </>
                        : <>
                            <p className="card-text">
                                Pick something at least {MIN_LENGTH} characters long that you do not
                                use anywhere else.
                            </p>

                            <form onSubmit={submit}>
                                <div>
                                    <label htmlFor="new-password">New password</label>
                                    <input
                                        id="new-password"
                                        name="new-password"
                                        type="password"
                                        className="form-control form-control-lg"
                                        value={password}
                                        onChange={(e) => { setPassword(e.currentTarget.value); if (error) setError(''); }}
                                        autoComplete="new-password"
                                        autoFocus
                                        disabled={busy} />
                                    <small className="form-text text-muted">
                                        {password.length}/{MIN_LENGTH} characters minimum
                                    </small>
                                </div>

                                <div>
                                    <label htmlFor="confirm-password">Repeat it</label>
                                    <input
                                        id="confirm-password"
                                        name="confirm-password"
                                        type="password"
                                        className="form-control form-control-lg"
                                        value={confirm}
                                        onChange={(e) => { setConfirm(e.currentTarget.value); if (error) setError(''); }}
                                        autoComplete="new-password"
                                        disabled={busy} />
                                </div>

                                {error &&
                                    <div className="notice notice--warn" role="alert">{error}</div>}

                                <button type="submit"
                                    className="btn btn--primary btn--block"
                                    disabled={busy || password === '' || confirm === ''}>
                                    {busy
                                        ? <>Saving...</>
                                        : <>Set my new password</>}
                                </button>
                            </form>

                            <button type="button"
                               
                                onClick={onDone}>
                                Cancel
                            </button>
                          </>}
                </div>
            </div>
        </div>
    );
};

ResetPassword.propTypes = {
    token: PropTypes.string.isRequired,
    onDone: PropTypes.func.isRequired,
};

export default ResetPassword;
